import { supabase } from '@/integrations/supabase/client';

export type BodyHealthRecordGuardState = 'corrected' | 'deleted';
export interface BodyHealthRecordGuardInfo {
  guard_verified: boolean;
  user_corrected: boolean;
  guard_state: BodyHealthRecordGuardState | null;
}
interface GuardQuery extends PromiseLike<{ data: unknown; error: unknown }> {
  select(columns: string): GuardQuery;
  eq(column: string, value: string): GuardQuery;
  in(column: string, values: string[]): GuardQuery;
  setHeader(name: string, value: string): GuardQuery;
  abortSignal(signal: AbortSignal): GuardQuery;
}
const guardClient = supabase as unknown as { from(table: 'body_health_record_guards'): GuardQuery };
const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

/** Read only correction/deletion labels, never the private origin key or payload. */
export async function readBodyHealthRecordGuards(ownerId: string, resource: 'body' | 'health', recordIds: string[], options: {
  assertCurrent: () => void; bearer?: string; signal?: AbortSignal;
}) {
  if (!uuid(ownerId) || recordIds.some(id => !uuid(id))) throw new Error('Invalid body health guard lookup');
  const ids = [...new Set(recordIds)], result = new Map<string, BodyHealthRecordGuardState>();
  for (let offset = 0; offset < ids.length; offset += 200) {
    const batch = ids.slice(offset, offset + 200), expected = new Set(batch);
    options.assertCurrent();
    let query = guardClient.from('body_health_record_guards').select('user_id,resource,record_id,state')
      .eq('user_id', ownerId).eq('resource', resource).in('record_id', batch);
    if (options.bearer) query = query.setHeader('Authorization', 'Bearer ' + options.bearer);
    if (options.signal) query = query.abortSignal(options.signal);
    const { data, error } = await query;
    options.assertCurrent();
    if (error || !Array.isArray(data)) throw error ?? new Error('Body health guards not confirmed');
    for (const raw of data) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid body health guard');
      const row = raw as Record<string, unknown>;
      if (row.user_id !== ownerId || row.resource !== resource || !uuid(row.record_id) || !expected.has(row.record_id) || !['corrected', 'deleted'].includes(String(row.state))) throw new Error('Body health guard owner or record changed');
      const state = row.state as BodyHealthRecordGuardState;
      if (result.has(row.record_id) && result.get(row.record_id) !== state) throw new Error('Conflicting body health guards');
      result.set(row.record_id, state);
    }
  }
  return result;
}

export function withBodyHealthRecordGuards<T extends { id: string }>(records: T[], guards: Map<string, BodyHealthRecordGuardState>): (T & BodyHealthRecordGuardInfo)[] {
  return records.filter(row => guards.get(row.id) !== 'deleted').map(row => ({ ...row,
    guard_verified: true, user_corrected: guards.get(row.id) === 'corrected', guard_state: guards.get(row.id) ?? null,
  }));
}

/** Corrected imports retain their provenance, but cannot establish a provider scan comparison. */
export function comparableBodyMeasurements<T extends { source: string; scan_provider: string | null; measured_at: string; user_corrected?: boolean; guard_verified?: boolean }>(records: T[]): T[] {
  const latest = records[0];
  if (!latest || latest.guard_verified !== true || latest.source === 'scan' && latest.user_corrected || !((latest.source === 'manual' && latest.scan_provider === null) || (latest.source === 'scan' && latest.scan_provider === '3dlook'))) return [];
  const instants = new Set<number>();
  return records.filter(row => {
    const instant = Date.parse(row.measured_at);
    if (row.guard_verified !== true || row.source === 'scan' && row.user_corrected || row.source !== latest.source || row.scan_provider !== latest.scan_provider || !Number.isFinite(instant) || instants.has(instant)) return false;
    instants.add(instant); return true;
  });
}
